import React, { useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { postCustomer } from "../../../../../shared/services/services";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import InputGroup from "./InputGroup";
import { textColorOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { errorHandling } from "src/shared/services/errorHandling";

type Props = {
  close: (data: any) => void;
  loading: (data: boolean) => void;
  addCustomer: boolean;
};

const AddCustomer: React.FC<Props> = ({ close, loading, addCustomer }) => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    alternativenumber: "",
    Email: "",
    customeraddress1: "",
    customeraddress2: "",
    customeraddress3: "",
    osb: "",
  });

  const [showMore, setShowMore] = useState(false);
  const { user } = useUser();
  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["-", "e", "E", "+", ".", "(", ")"].includes(e.key)) e.preventDefault();
  };

  const handleConfirm = async () => {
    const { name, phoneNumber, alternativenumber, Email, customeraddress1, customeraddress2, customeraddress3, osb } = formData;

    if (!phoneNumber.trim()) {
      notifyError("Primary Number are required.");
      return;
    }

    const customerData = {
      Name: name,
      Email,
      phone_number: phoneNumber,
      alternativenumber,
      Out_standing_balance: Number(osb) ?? 0,
      Address: { customeraddress1, customeraddress2, customeraddress3 },
    };

    try {
      loading(true);
      const res = await postCustomer(customerData);
      notifySuccess("Customer added successfully!");
      Clearform();
      close(res);
    } catch (error: any) {
      errorHandling(error);
    } finally {
      loading(false);
    }
  };

  const Clearform = () => {
    setFormData({
      name: "",
      phoneNumber: "",
      alternativenumber: "",
      Email: "",
      customeraddress1: "",
      customeraddress2: "",
      customeraddress3: "",
      osb: "",
    });
    setShowMore(false);
  };

  const btnDisabled =
    formData.phoneNumber.length < 8 ||
    formData.phoneNumber.length > 12 ||
    formData.name.trim() === "" ||
    formData.phoneNumber === formData.alternativenumber;

  return (
    <PopupModules
      isOpen={addCustomer}
      onClose={() => {
        Clearform();
        close("");
      }}
      title='Add New Customer'
    >
      <div className='flex flex-col gap-3 p-2 sm:p-4'>
        {/* Basic Fields */}
        <div className='space-y-3'>
          <InputGroup label='Customer Name' type='text' value={formData.name} name='name' onChange={handleChange} />
          <InputGroup
            label='Primary Number (8 to 12 digits on numbers)'
            type='number'
            value={formData.phoneNumber}
            name='phoneNumber'
            onChange={handleChange}
            onKeyDown={handleNumberKey}
            mandatory={true}
          />
        </div>

        {/* Toggle for More Details */}
        <div className='w-full flex justify-end mt-1'>
          <button
            onClick={() => setShowMore(!showMore)}
            className={`text-sm font-medium hover:underline ${textColorOptions[user?.background_colour ?? "Blue"]}`}
          >
            {showMore ? "Hide Details ▲" : "More Details ▼"}
          </button>
        </div>

        {/* Expanded Section */}
        {showMore && (
          <div className='border-t border-gray-200 pt-3 mt-2'>
            <InputGroup label='Email ID' type='email' value={formData.Email} name='Email' onChange={handleChange} />
            <InputGroup
              label='Secondary Number'
              type='number'
              value={formData.alternativenumber}
              name='alternativenumber'
              onChange={handleChange}
              onKeyDown={handleNumberKey}
              error={formData.phoneNumber === formData.alternativenumber ? true : false}
            />
            <InputGroup
              label='Out_standing_balance'
              type='number'
              value={formData.osb}
              name='osb'
              onChange={handleChange}
              onKeyDown={handleNumberKey}
            />

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <InputGroup label='Address 1' type='text' value={formData.customeraddress1} name='customeraddress1' onChange={handleChange} />
              <InputGroup label='Address 2' type='text' value={formData.customeraddress2} name='customeraddress2' onChange={handleChange} />
              <InputGroup label='Address 3' type='text' value={formData.customeraddress3} name='customeraddress3' onChange={handleChange} />
            </div>
          </div>
        )}

        {formData.phoneNumber === formData.alternativenumber && formData.phoneNumber !== "" && (
          <p className='text-red-600 text-sm'>Primary and Secondary numbers should not be the same.</p>
        )}

        {/* Action Buttons */}
        <div className='flex justify-center gap-4 pt-4 border-t border-gray-200 mt-3'>
          <Button
            label='Cancel'
            variant='ThemeCancel'
            className='border'
            onClick={() => {
              Clearform();
              close("");
            }}
          />
          <Button label='Confirm' variant='ThemeBase' onClick={handleConfirm} disabled={btnDisabled} />
        </div>
      </div>
    </PopupModules>
  );
};

export default AddCustomer;
